import React from 'react';

import UploadSpreadsheetForm from 'bulk-import/panes/UploadSpreadsheetForm';
import DataConnector from 'connect/DataConnector';
import {ModalPane} from 'constants/SampleConstants';
import notifications from 'notifications';
import {ContainerSchema, PlateSchema} from 'samples/storables/StorableSchemaConstants';
import * as TagUtil from 'schemas/tags/TagUtil';
import FormGroup from 'ui-library/bs/FormGroup';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import * as ImportUtil from 'util/ImportUtil';

import BaseStorablePane, {
  BaseStorablePaneProps,
  CreateStorablePaneState,
  DEFAULT_CREATE_STORABLE_STATE,
} from './BaseStorablePane';
import BaseStorablePaneStudyableFormGroup, {
  BaseStorablePaneStudyableProps,
  BaseStorablePaneStudyableState,
} from './BaseStorablePaneStudyableFormGroup';
import ContainerSchemaSelector from './ContainerSchemaSelector';
import PlateSchemaSelector, {PlateSchemaSelectorProps} from './PlateSchemaSelector';
import * as SampleSchemaActions from './SampleSchemaActions';
import SampleSchemaStore from './SampleSchemaStore';
import * as SampleUtil from './SampleUtil';

const UPLOAD_WELLS_HELP_TEXT =
  'You may optionally upload a spreadsheet to create wells with your matrix plate. To do so, attach a' +
  ' spreadsheet below with each row specifying a "Container Barcode" and "Destination Well"';

class CreatePlatePaneConnector extends DataConnector<CreatePlatePaneProps, EnhancedProps> {
  override shouldSubscribeToRedux = false;
  override stores = [SampleSchemaStore];

  enhanceProps(): EnhancedProps {
    if (!this.props.registryId) {
      return {};
    }
    return {
      containerSchemas: this.sampleSchemaStore().getContainerSchemas(this.props.registryId),
    };
  }
}

export interface CreatePlatePaneProps
  extends Omit<BaseStorablePaneProps, 'nameLabel' | 'modalPane' | 'schemaSelector'>,
    BaseStorablePaneStudyableProps {
  disablePlateSchemaAndRegistrySelectors?: boolean;
  disabledPlateSchemaSelectorMessage?: string;
  disabledRegistrySelectorMessage?: string;
  selectedContainerSchema?: ContainerSchema | null;
  selectedSchema?: PlateSchema | null;
  spreadsheetFile?: File | null;
  spreadsheetUploadHelpText?: string;
  plateSchemaSelectorProps?: Pick<PlateSchemaSelectorProps, 'plateSchemaType' | 'plateSchemaFilter'>;
}
interface EnhancedProps {
  containerSchemas?: Array<ContainerSchema> | null;
}

export interface CreatePlateSpreadsheetConfig {
  selectedContainerSchema: ContainerSchema | null;
  spreadsheetFile: File | null;
  spreadsheetURL: string | null;
}

export interface CreatePlatePaneState
  extends CreateStorablePaneState,
    CreatePlateSpreadsheetConfig,
    BaseStorablePaneStudyableState {
  isValidating: boolean;
  selectedSchema: PlateSchema | null;
}

export const DEFAULT_CREATE_PLATE_PANE_STATE: CreatePlatePaneState = {
  ...DEFAULT_CREATE_STORABLE_STATE,
  isValidating: false,
  selectedSchema: null,
  selectedContainerSchema: null,
  spreadsheetFile: null,
  spreadsheetURL: null,
  studyIds: [],
};

class CreatePlatePane extends React.Component<CreatePlatePaneProps & EnhancedProps> {
  static defaultProps = {
    spreadsheetUploadHelpText: UPLOAD_WELLS_HELP_TEXT,
  };

  override componentDidMount(): void {
    if (this.props.selectedSchema != null && this.props.tags.length === 0) {
      this._handleChangePlateSchema(this.props.selectedSchema);
    }

    if (!this.props.containerSchemas) {
      SampleSchemaActions.fetchAllContainerSchemas();
    }
  }

  _handleChangePlateSchema = (schema: PlateSchema): void => {
    const updateProps = {
      selectedSchema: schema,
      selectedContainerSchema:
        this.props.containerSchemas?.find(
          (containerSchema) => containerSchema.id === schema.containerSchemaId
        ) ?? null,
      tags: TagUtil.getInitialTags(schema.fields),
    };
    this.props.onUpdate(updateProps);
  };

  _handleChangeContainerSchema = (selectedContainerSchema: ContainerSchema | null): void => {
    this.props.onUpdate({selectedContainerSchema});
  };

  _renderSchemaSelector = (): JSX.Element => (
    <PlateSchemaSelector
      disabled={this.props.disablePlateSchemaAndRegistrySelectors}
      disabledMessage={this.props.disabledPlateSchemaSelectorMessage}
      error={this.props.validationErrors.schemaError}
      onChange={this._handleChangePlateSchema}
      registryId={this.props.registryId}
      selectedPlateSchemaId={this.props.selectedSchema ? this.props.selectedSchema.id : null}
      {...this.props.plateSchemaSelectorProps}
    />
  );

  _handleUploadWellSpreadsheet = (spreadsheetFile: File): void => {
    this.props.onUpdate({spreadsheetFile});
    ImportUtil.upload(spreadsheetFile)
      .done((url) => {
        this.props.onUpdate({spreadsheetURL: url});
      })
      .fail(notifications.notifyError);
  };

  _handleUnsetWellSpreadsheet = (): void => {
    this.props.onUpdate({
      validationErrors: {},
      isCreating: false,
      spreadsheetFile: null,
      spreadsheetURL: null,
    });
  };

  override render(): JSX.Element {
    if (!this.props.registryId || !this.props.containerSchemas) {
      return <CenteredHorizontalThrobber />;
    }
    return (
      <BaseStorablePane
        nameLabel="Plate name"
        modalPane={ModalPane.CREATE_PLATE}
        schemaSelector={this._renderSchemaSelector()}
        disableRegistrySelector={this.props.disablePlateSchemaAndRegistrySelectors}
        disabledRegistrySelectorTooltip={this.props.disabledRegistrySelectorMessage}
        {...this.props}
      >
        {SampleUtil.isMatrixPlateSchema(this.props.selectedSchema) &&
          !this.props.selectedSchema!.containerSchemaId && (
            <FormGroup>
              <FieldLabel isRequired={true} label="Container schema" />
              <ContainerSchemaSelector
                error={this.props.validationErrors.containerSchemaError}
                onChange={this._handleChangeContainerSchema}
                registryId={this.props.registryId}
                selectedContainerSchemaId={
                  this.props.selectedContainerSchema ? this.props.selectedContainerSchema.id : null
                }
              />
            </FormGroup>
          )}
        {SampleUtil.isMatrixPlateSchema(this.props.selectedSchema) && (
          <FormGroup>
            <UploadSpreadsheetForm
              exampleImageURL="/static/img/spreadsheet-import-example-upload-wells.png"
              file={this.props.spreadsheetFile}
              fileWord="well"
              helpText={this.props.spreadsheetUploadHelpText}
              onRemoveFile={this._handleUnsetWellSpreadsheet}
              onSelectFile={this._handleUploadWellSpreadsheet}
            />
          </FormGroup>
        )}
        <BaseStorablePaneStudyableFormGroup
          studySearchInputProps={this.props.studySearchInputProps}
          studyIds={this.props.studyIds}
          onUpdate={this.props.onUpdate}
        />
      </BaseStorablePane>
    );
  }
}

export default CreatePlatePaneConnector.connect(CreatePlatePane);
